import React, { useEffect, useState } from 'react';
import * as produtosApi from '../../api/produtos';
import { useEmpresaSelecionada } from '../../contexts/EmpresaContext';
import PageHeader from '../../components/common/PageHeader';
import EmptyState from '../../components/common/EmptyState';
import Skeleton from '../../components/common/Skeleton';
import SearchableSelect from '../../components/common/SearchableSelect';
import StatusBadge from '../../components/common/StatusBadge';
import { useSortable } from '../../hooks/useSortable';
import { getErrorMessage } from '../../utils/apiError';

export default function Produtos() {
  const { modoGlobal, empresas, idEmpresa } = useEmpresaSelecionada();

  const [itens, setItens] = useState([]);
  const [loading, setLoading] = useState(true);
  const [erro, setErro] = useState('');
  const [nome, setNome] = useState('');
  const [estoqueMinimo, setEstoqueMinimo] = useState('');
  const [idEmpresaForm, setIdEmpresaForm] = useState('');
  const [editando, setEditando] = useState(null);
  const [mostrarForm, setMostrarForm] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [busca, setBusca] = useState('');
  const [mostrarInativos, setMostrarInativos] = useState(false);

  const carregar = () => {
    setLoading(true);
    produtosApi
      .listProdutos(mostrarInativos)
      .then((r) => setItens(r.data))
      .catch(() => setErro('Erro ao carregar produtos.'))
      .finally(() => setLoading(false));
  };

  // idEmpresa muda quando o super_admin troca de empresa no seletor do topbar — sem isso, a
  // lista continuaria mostrando os cadastros da empresa anterior até uma navegação manual.
  useEffect(carregar, [mostrarInativos, idEmpresa]);

  const filtrados = itens.filter((i) => i.nome.toLowerCase().includes(busca.toLowerCase()));
  const { sorted, Th } = useSortable(filtrados, 'nome');

  const abrirNovo = () => {
    setNome('');
    setEstoqueMinimo('');
    setIdEmpresaForm('');
    setEditando(null);
    setMostrarForm(true);
  };

  const abrirEdicao = (item) => {
    setNome(item.nome);
    setEstoqueMinimo(item.estoque_minimo ?? '');
    setEditando(item);
    setMostrarForm(true);
  };

  const alternarAtivo = async (item) => {
    const acao = item.ativo ? 'Desativar' : 'Reativar';
    const aviso = item.ativo ? ' Ele deixa de aparecer nesta lista e nos seletores de busca.' : '';
    if (!window.confirm(`${acao} "${item.nome}"?${aviso}`)) return;
    setErro('');
    try {
      await produtosApi.updateProduto(item.id_produto, { ativo: !item.ativo }, item.id_empresa);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao alterar o status do produto.'));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErro('');
    if (!editando && modoGlobal && !idEmpresaForm) {
      setErro('Selecione a empresa dona deste cadastro.');
      return;
    }
    setSalvando(true);
    const dados = { nome, estoque_minimo: estoqueMinimo === '' ? null : Number(estoqueMinimo) };
    try {
      if (editando) await produtosApi.updateProduto(editando.id_produto, dados, editando.id_empresa);
      else await produtosApi.createProduto(dados, idEmpresaForm);
      setMostrarForm(false);
      carregar();
    } catch (err) {
      setErro(getErrorMessage(err, 'Erro ao salvar.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div>
      <PageHeader titulo="Produtos" subtitulo="Batata, cebola, tomate...">
        {!mostrarForm && (
          <button type="button" className="sf-btn sf-btn-primary" onClick={abrirNovo}>
            <i className="bi bi-plus-lg" />
            Novo produto
          </button>
        )}
      </PageHeader>

      {erro && (
        <div className="sf-login-alert mb-3">
          <i className="bi bi-exclamation-circle" />
          {erro}
        </div>
      )}

      {mostrarForm && (
        <form onSubmit={handleSubmit} className="sf-card p-4 mb-3" style={{ maxWidth: 480 }}>
          {editando && modoGlobal && (
            <div className="sf-badge sf-badge-neutral sf-badge-plain mb-3">
              <i className="bi bi-building" /> {editando.empresa.nome}
            </div>
          )}
          {!editando && modoGlobal && (
            <div className="sf-field-label mb-3">
              <span>Empresa</span>
              <SearchableSelect
                options={empresas.map((e) => ({ value: e.id_empresa, label: e.nome }))}
                value={idEmpresaForm}
                onChange={setIdEmpresaForm}
                placeholder="Selecione a empresa..."
                required
              />
            </div>
          )}
          <label className="sf-field-label">
            <span>Nome</span>
            <span className="sf-field">
              <input required autoFocus value={nome} onChange={(e) => setNome(e.target.value)} />
            </span>
          </label>
          <label className="sf-field-label mt-3">
            <span>Estoque mínimo</span>
            <span className="sf-field">
              <input
                type="number"
                min="0"
                step="0.001"
                placeholder="Deixe em branco para não alertar"
                value={estoqueMinimo}
                onChange={(e) => setEstoqueMinimo(e.target.value)}
              />
            </span>
          </label>
          <div className="mt-3 d-flex gap-2">
            <button type="submit" className="sf-btn sf-btn-primary" disabled={salvando}>
              {salvando ? 'Salvando...' : 'Salvar'}
            </button>
            <button type="button" className="sf-btn sf-btn-ghost" onClick={() => setMostrarForm(false)}>
              Cancelar
            </button>
          </div>
        </form>
      )}

      <div className="sf-table-card">
        <div className="sf-table-toolbar">
          <div className="sf-search">
            <i className="bi bi-search" />
            <input placeholder="Buscar produto..." value={busca} onChange={(e) => setBusca(e.target.value)} />
          </div>
          <label className="d-flex align-items-center gap-2" style={{ cursor: 'pointer', whiteSpace: 'nowrap' }}>
            <input type="checkbox" checked={mostrarInativos} onChange={(e) => setMostrarInativos(e.target.checked)} />
            Mostrar inativos
          </label>
        </div>
        {loading ? (
          <div className="p-4">
            <Skeleton linhas={5} />
          </div>
        ) : sorted.length === 0 ? (
          <EmptyState icon="bi-basket" titulo="Nenhum produto encontrado" texto="Ajuste a busca ou cadastre um novo produto." />
        ) : (
          <div className="table-responsive">
            <table className="sf-table">
              <thead>
                <tr>
                  <Th campo="nome">Nome</Th>
                  <th>Estoque mínimo</th>
                  {modoGlobal && <th>Empresa</th>}
                  {mostrarInativos && <th>Status</th>}
                  <th />
                </tr>
              </thead>
              <tbody>
                {sorted.map((p) => (
                  <tr key={p.id_produto}>
                    <td>
                      <span className="d-flex align-items-center gap-2">
                        <span className="sf-cell-icon"><i className="bi bi-basket" /></span>
                        <span className="sf-cell-title">{p.nome}</span>
                      </span>
                    </td>
                    <td>{p.estoque_minimo ?? '—'}</td>
                    {modoGlobal && <td className="sf-cell-sub">{p.empresa.nome}</td>}
                    {mostrarInativos && <td><StatusBadge status={p.ativo ? 'ativo' : 'inativo'} /></td>}
                    <td className="text-end">
                      <div className="d-flex gap-2 justify-content-end">
                        <button type="button" className="sf-btn sf-btn-ghost" onClick={() => abrirEdicao(p)} title="Editar">
                          <i className="bi bi-pencil" />
                        </button>
                        <button
                          type="button"
                          className="sf-btn sf-btn-ghost"
                          style={p.ativo ? { color: 'var(--sf-danger-fg)', borderColor: '#f0cfcc' } : undefined}
                          onClick={() => alternarAtivo(p)}
                          title={p.ativo ? 'Desativar' : 'Reativar'}
                        >
                          <i className={`bi ${p.ativo ? 'bi-slash-circle' : 'bi-check-circle'}`} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
